// client/src/contexts/LanguageContext.tsx
import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import {
  loadTranslations,
  getStoredLanguage,
  setUserLanguagePreference,
  SUPPORTED_LANGUAGES,
} from '../services/translationService';
import LoadingState from '../components/common/LoadingState';

export type TranslationsMap = Record<string, string>;

interface LanguageContextType {
  currentLanguage: string;
  t: (key: string, fallback?: string) => string;
  changeLanguage: (lang: string) => Promise<void>;
  languageNames: { [key: string]: string };
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<string>(getStoredLanguage());
  const [translations, setTranslations] = useState<TranslationsMap | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const languageNames = SUPPORTED_LANGUAGES.reduce((acc, lang) => {
    acc[lang.code] = lang.name;
    return acc;
  }, {} as { [key: string]: string });

  useEffect(() => {
    const fetchTranslations = async () => {
      setIsLoading(true);
      try {
        const loadedTranslations = await loadTranslations(currentLanguage);
        setTranslations(loadedTranslations);
      } catch (error) {
        console.error('Failed to load translations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTranslations();
  }, [currentLanguage]);

  const t = (key: string, fallback?: string): string => {
    if (!translations) {
      return fallback || key;
    }
    return translations[key] || fallback || key;
  };

  const changeLanguage = async (lang: string): Promise<void> => {
    if (lang !== currentLanguage) {
      setIsLoading(true); // Set loading state when language changes
      const success = await setUserLanguagePreference(lang);
      if (success) {
        setCurrentLanguage(lang);
      } else {
        setIsLoading(false); // Reset loading if language change failed
      }
    }
  };

  useEffect(() => {
    const storedLang = getStoredLanguage();
    setCurrentLanguage(storedLang);
  }, []);

  // Render loading state if translations aren't loaded yet
  if (isLoading || !translations) {
    return <LoadingState message={`Translations loading...`} />;
  }

  return (
    <LanguageContext.Provider value={{ currentLanguage, t, changeLanguage, languageNames, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};